import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/api/client";
import { toast } from "sonner";

export function UpgradeDialog({ open, onOpenChange, reason }) {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("annual");

  const handleRequestUpgrade = async () => {
    setLoading(true);
    try {
      await api.post("/upgrade-request", { plan: selectedPlan });
      setSent(true);
      toast.success("Upgrade request sent");
    } catch (e) {
      toast.error("Could not send your upgrade request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (v) => {
    if (!v) setSent(false);
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="upgrade-dialog">
        {sent ? (
          <div className="text-center py-4" data-testid="upgrade-request-sent-panel">
            <CheckCircle2 className="text-emerald-500 mx-auto mb-3" size={32} />
            <h3 className="font-semibold text-slate-900 mb-1">Request sent</h3>
            <p className="text-sm text-slate-500 mb-4">Someone from ModernTect will reach out to set up your subscription.</p>
            <Button onClick={() => handleClose(false)} data-testid="upgrade-request-done-button" className="bg-blue-600 hover:bg-blue-700">
              Got it
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Upgrade to ModernTect Unlimited</DialogTitle>
              <DialogDescription>{reason || "Unlock unlimited projects and views."}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-1" data-testid="upgrade-pricing-table">
              <button
                type="button"
                onClick={() => setSelectedPlan("monthly")}
                data-testid="upgrade-pricing-monthly"
                className={`text-left rounded-lg p-3 border-2 transition-colors ${
                  selectedPlan === "monthly" ? "border-blue-500" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="text-xs font-medium text-slate-500 mb-1">Monthly</div>
                <div className="text-2xl font-bold text-slate-900">
                  £9.00<span className="text-sm font-normal text-slate-400">/mo</span>
                </div>
                <div className="text-xs text-slate-400 mt-1">£7.50 excl. VAT + £1.50 VAT (20%)</div>
              </button>
              <button
                type="button"
                onClick={() => setSelectedPlan("annual")}
                data-testid="upgrade-pricing-annual"
                className={`relative text-left rounded-lg p-3 border-2 transition-colors ${
                  selectedPlan === "annual" ? "border-blue-500" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <span
                  className="absolute -top-2 right-2 bg-blue-600 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                  data-testid="upgrade-pricing-savings-badge"
                >
                  Save £10.80/yr
                </span>
                <div className="text-xs font-medium text-slate-500 mb-1">Annual, paid upfront</div>
                <div className="text-2xl font-bold text-slate-900">
                  £8.10<span className="text-sm font-normal text-slate-400">/mo</span>
                </div>
                <div className="text-xs text-slate-400 mt-1">£81.00 excl. VAT + £16.20 VAT = £97.20/yr</div>
              </button>
            </div>
            <p className="text-xs text-slate-400">
              Per user, in GBP. The annual plan saves £10.80/user/year vs. £108.00 paid over 12 monthly payments.
            </p>
            <ul className="text-sm text-slate-600 space-y-1.5 py-2 list-disc pl-5">
              <li>Unlimited projects and views</li>
              <li>Everything on the free plan, with no caps</li>
            </ul>
            <p className="text-xs text-slate-400">Upgrades are currently arranged manually - request one below and we'll be in touch.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)} data-testid="upgrade-dialog-cancel-button">
                Not now
              </Button>
              <Button
                onClick={handleRequestUpgrade}
                disabled={loading}
                data-testid="upgrade-dialog-confirm-button"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
                Request Upgrade ({selectedPlan === "annual" ? "Annual" : "Monthly"})
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
